import React from 'react';
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useAnalytics from '../hooks/useAnalytics';

// Constants
const CTA_CONTENT = {
  heading: "What if you could cut the busywork and unlock more revenue?",
  buttonText: "Book a demo",
  subText: "Let us show you how!"
};

const CallToActionV1 = () => {
  const analytics = useAnalytics();
  const navigate = useNavigate();

  // Generate structured data for Action schema
  const actionSchema = {
    "@context": "https://schema.org",
    "@type": "Action",
    "name": "Book a Product Demo",
    "description": "Schedule a demonstration of Strive's AI-powered product management assistant",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://strive.us/contact",
      "actionPlatform": [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform"
      ]
    },
    "potentialAction": {
      "@type": "ScheduleAction",
      "name": "Book Demo"
    }
  };

  const handleCalendarRequest = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.trackFormSubmission({
      formType: 'demo',
      formLocation: 'cta-section',
      formSuccess: true
    });
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <meta 
          name="description" 
          content="Book a demo to see how Strive's AI-powered assistant can help you cut busywork and unlock more revenue."
        />
        <meta property="og:title" content="Book a Demo - Strive AI Assistant" />
        <meta 
          property="og:description" 
          content="Transform your workflow and boost revenue with Strive. Schedule a demo today!"
        />
        <script type="application/ld+json">
          {JSON.stringify(actionSchema)}
        </script>
      </Helmet>

      <section 
        className="relative py-24 bg-gradient-to-b from-gray-100 via-white to-white"
        aria-labelledby="cta-heading"
      >
        {/* Decorative elements */}
        <div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          aria-hidden="true"
        >
          <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
        </div>

        <div className="container relative mx-auto px-4 text-center">
          <h2 
            id="cta-heading"
            className="text-4xl md:text-5xl font-bold mb-12 max-w-4xl mx-auto text-gray-900"
          >
            {CTA_CONTENT.heading}
          </h2>
          
          <div 
            className="flex flex-col items-center space-y-6"
            role="group"
            aria-labelledby="cta-heading"
          >
            <Button 
              className="px-16 py-8 text-2xl font-medium bg-primary text-white rounded-2xl shadow-lg hover:bg-primary/90 hover:shadow-xl hover:translate-y-[-2px] transition-all duration-300 ease-in-out border-0"
              onClick={handleCalendarRequest}
              aria-label="Schedule a product demonstration"
            >
              {CTA_CONTENT.buttonText}
            </Button>
            
            <p 
              className="text-lg text-gray-600 font-semibold"
              aria-live="polite"
            >
              {CTA_CONTENT.subText}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default CallToActionV1;
